import React from 'react';
import {
  Typography,
  Box,
  Container,
  Paper,
  Divider,
  Link,
  styled,
  alpha,
} from '@mui/material';

const MailTo = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecorationColor: alpha(theme.palette.primary.main, 0.4),

  ':hover': {
    textDecorationColor: theme.palette.primary.main,
  },
}));

const About = () => {
  return (
    <Container maxWidth="md">
      <Box sx={{ color: 'white' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          About KIP
        </Typography>

        <Typography variant="body1" paragraph>
          KIP is a music streaming platform dedicated to the journey of
          electronic music.
        </Typography>

        <Typography variant="body1" paragraph>
          Producers, independent labels, DJs, venues, promoters and fans all
          play an important role in this journey, but not all are equally
          respected on other streaming platforms.
        </Typography>

        <Typography variant="body1" paragraph>
          KIP is the first platform to recognize, respect and work towards the
          goals of every member of the electronic music community:
        </Typography>

        <Box component="ul" sx={{ pl: 4, mb: 3 }}>
          <Typography component="li" paragraph>
            Monetize recorded sets for{' '}
            <Link to="/artists" underline="always">
              DJs
            </Link>
          </Typography>
          <Typography component="li" paragraph>
            Drive sales of tracks for{' '}
            <Link to="/artists" underline="always">
              Producers
            </Link>
          </Typography>
          <Typography component="li" paragraph>
            Grow fanbases for{' '}
            <Link to="/labels" underline="always">
              Labels
            </Link>{' '}
            and Curators (promoters, venues, etc.)
          </Typography>
          <Typography component="li" paragraph>
            Curate a better listening experience than algorithms for Fans
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          We are striving to be an inclusive platform for all members of the
          electronic community. Explore more of what we believe in below.
        </Typography>

        <Paper
          elevation={3}
          sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        >
          <Typography variant="h5" gutterBottom>
            Artists (Producers & DJs)
          </Typography>
          <Typography variant="body1" paragraph>
            Producers often do not get recognition when their tracks are played
            in DJ sets. KIP is changing that paradigm by automatically adding
            track lists for RELEASED music. This will enable us to pay both DJs
            and Producers for streams of sets.
          </Typography>
          <Typography variant="body1">
            When unreleased tracks (IDs) are released (currently on Beatport),
            all of the fans that liked that ID will get a notification. This
            enables KIP to respect the secret weapons of DJs while giving
            producers recognition when their track is released for sale.
          </Typography>
        </Paper>

        <Paper
          elevation={3}
          sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        >
          <Typography variant="h5" gutterBottom>
            Independent Labels
          </Typography>
          <Typography variant="body1">
            Labels do not have representation on other streaming platforms. This
            is a huge issue for electronic music - where new artists often rise
            to fame because they are released on a specific label. By making
            Labels a central part of KIP, all of the benefits that Artists enjoy
            on other platforms are now available to Labels to grow their
            fanbase, support their artists and spread their music!
          </Typography>
        </Paper>

        <Paper
          elevation={3}
          sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        >
          <Typography variant="h5" gutterBottom>
            Curators (Promoters, Venues, Festivals, etc)
          </Typography>
          <Typography variant="body1">
            Everyone knows the names of big festivals and top tier clubs - and
            artists playing in these venues will often promote themselves by
            recording parts of their sets. But this support can go both ways as
            promoters strive to curate a sound and reputation. On KIP, curators
            are recognized with their own profile that links to all of the sets
            recorded live (or for promotional purposes) under their name. This
            enables an essential part of the electronic scene to grow alongside
            the artists they support.
          </Typography>
        </Paper>

        <Paper
          elevation={3}
          sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        >
          <Typography variant="h5" gutterBottom>
            Fans (Ravers, Clubheads, Everyone)
          </Typography>
          <Typography variant="body1" paragraph>
            Music unites people from all walks of life. That is the original
            inspiration for KIP - how do you share music among your friends and
            make meaningful connections on nights out?
          </Typography>
          <Typography variant="body1">
            By focusing our platform around DJ sets, you get to re-experience
            moments of euphoria from a recorded live set or listen to a curated
            journey by a professional artist - not a soulless algorithm. As you
            listen you can seamlessly like tracks 🖤 that are then organized
            into a personal library that constantly updates as IDs are released.
          </Typography>
        </Paper>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="body1" paragraph>
          KIP will charge a monthly subscription, but by{' '}
          <Link to="/artists" underline="register">
            signing up now
          </Link>{' '}
          you can experience the Beta for free as we build towards a future that
          respects, recognizes and ultimately pays the artists that make life
          worth living.
        </Typography>

        <Typography variant="body1">
          Please reach out to the{' '}
          <MailTo href="mailto:kip@kip.audio">KIP Team</MailTo> with any
          suggestions or feedback.
        </Typography>
      </Box>
    </Container>
  );
};

export default About;
